// @flow
import React, { Fragment, PureComponent } from 'react';
import { getMonthName } from 'utils/dateUtils';
import { dynamicClassName } from 'utils/dynamicClassName';
import DropdownRadioInput from 'components/fragments/input/DropdownRadioInput';
import DropdownInput from 'components/fragments/input/DropdownInput';
import { EVENT_NAVBAR_SIZE_MOBILE } from 'constants/eventConstants';
import { TYPE_NORMAL, TYPE_MOBILE } from 'constants/menuConstants';
import ButtonIcon from 'components/fragments/button/ButtonIcon';
import { setBodyStyle, resetBodyStyle } from 'utils/bodyUtils';

type Props = {
  currentMonth: Date,
  labels: Array<string>,
  handleLinkClicked: (label: string) => void,
  handleMonthChanged: (date: string) => void,
  eventFilters: Array<{ label: string, value: string }>,
  handleEventFilterClicked: (filter: string) => void,
  selectedEventFilters: Array<string>,
  teamFilters: Array<{ label: string, value: string }>,
  federalFilters: Array<{label: string, value: string}>,
  selectedFederalFilters: Array<string>,
  handleFederalFilterClicked: (filter: string) => void,
  handleTeamFilterClicked: (filter: string) => void,
  selectedTeamFilters: Array<string>,
  handleResetFilters: () => void,
  label: string
};

type State = {
  activeLink: string,
  menuType: TYPE_MOBILE | TYPE_NORMAL,
  showFilters: boolean
};

class EventListNavbar extends PureComponent<Props, State> {
  _navbar: ?HTMLElement;
  props: Props;
  state: State = {
    activeLink: this.props.labels[0],
    menuType: TYPE_NORMAL,
    showFilters: false
  };

  componentDidMount() {
    this.updateDimensions();
    window.addEventListener('resize', this.updateDimensions.bind(this));
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions.bind(this));
  }

  updateDimensions = () => {
    if (document) {
      if (this.state.menuType === TYPE_NORMAL && window.innerWidth < EVENT_NAVBAR_SIZE_MOBILE)
        this.setState({ menuType: TYPE_MOBILE });
      else if (this.state.menuType === TYPE_MOBILE && window.innerWidth >= EVENT_NAVBAR_SIZE_MOBILE) {
        resetBodyStyle();
        this.setState({ menuType: TYPE_NORMAL, showFilters: false });
      }
    }
  };

  handleLinkClicked = (label: string) => (e: MouseEvent) => {
    e.preventDefault();
    this.setState({ activeLink: label });
    this.props.handleLinkClicked(label);
  };

  handleLabelClicked = (label: string) => {
    this.setState({ activeLink: label });
    this.props.handleLinkClicked(label);
  };

  getLabelClassName = (label: string) => {
    const classNamesLabel = dynamicClassName('');
    this.state.activeLink === label && classNamesLabel.add('is-active');
    return classNamesLabel.build();
  };

  getLastMonthFromCurrent = (currentMonth: Date) => {
    const lastMonth = new Date(currentMonth);
    if (lastMonth.getMonth() === 0) {
      lastMonth.setMonth(11);
      lastMonth.setFullYear(lastMonth.getFullYear() - 1);
    } else lastMonth.setMonth(lastMonth.getMonth() - 1);
    return lastMonth;
  };

  getNextMonthFromCurrent = (currentMonth: Date) => {
    const nextMonth = new Date(currentMonth);
    if (nextMonth.getMonth() === 11) {
      nextMonth.setMonth(0);
      nextMonth.setFullYear(nextMonth.getFullYear() + 1);
    } else nextMonth.setMonth(nextMonth.getMonth() + 1);
    return nextMonth;
  };

  getMonthsAsOptions = (baseMonth: Date): Array<{ value: string, label: string }> => {
    const previousMonths = [];

    let currentMonth = baseMonth;
    for (let i = 0; i < 5; i++) {
      currentMonth = this.getLastMonthFromCurrent(currentMonth);
      previousMonths.push(currentMonth);
    }

    const nextMonths = [];
    currentMonth = baseMonth;
    for (let i = 0; i < 5; i++) {
      currentMonth = this.getNextMonthFromCurrent(currentMonth);
      nextMonths.push(currentMonth);
    }

    return [...previousMonths.reverse(), baseMonth, ...nextMonths].map(month => ({
      value: month.toString(),
      label: `${getMonthName(month.getMonth())} ${month.getFullYear()}`
    }));
  };

  handleToggleFilter = () => {
    if (!this.state.showFilters) {
      setBodyStyle({
        position: 'fixed',
        width: '100%',
        top: 0
      });
    } else {
      resetBodyStyle();
    }
    this.setState(state => ({ showFilters: !state.showFilters }));
  };

  renderLabels = (): any => {
    if (this.state.menuType === TYPE_NORMAL) {
      return this.props.labels.map((label, index) => (
        <li key={index}>
          <a
            role="button"
            tabIndex={0}
            onClick={this.handleLinkClicked(label)}
            title={`Voir les éléments de la catégorie ${label}`}
            className={this.getLabelClassName(label)}
          >
            {label}
          </a>
        </li>
      ));
    }

    return null;
  };

  renderFilters = () => {
    const classNamesTeam = dynamicClassName('dropdown dropdown--radio');
    this.state.menuType === TYPE_MOBILE && classNamesTeam.add('mb-3');

    return (
      <Fragment>
        <DropdownInput
          label="Toutes les équipes"
          labelIcon="icon-jersey"
          counter={0}
          id="team_dropdown"
          options={this.props.teamFilters}
          inputChanged={this.props.handleTeamFilterClicked}
          selected={this.props.selectedTeamFilters}
          className={classNamesTeam.build()}
        />
        <DropdownInput
          label="Type d'évènements"
          counter={0}
          id="event_type_dropdown"
          options={this.props.eventFilters}
          inputChanged={this.props.handleEventFilterClicked}
          selected={this.props.selectedEventFilters}
          className={'dropdown dropdown--radio'}
        />
      </Fragment>
    );
  };

  renderFederalFilters = () => {
    const classNamesTeam = dynamicClassName('dropdown dropdown--radio');
    this.state.menuType === TYPE_MOBILE && classNamesTeam.add('mb-3');

    return (
      <Fragment>
        <DropdownInput
          label="Type d'évènements"
          counter={0}
          id="event_type_dropdown"
          options={this.props.federalFilters}
          inputChanged={this.props.handleFederalFilterClicked}
          selected={this.props.selectedFederalFilters}
          className={'dropdown dropdown--radio'}
        />
      </Fragment>
    );
  };

  renderLargeNavbar = () => {
    const { activeLink } = this.state;
    const { labels } = this.props;

    return (
      <Fragment>
        <nav className="navbar ">
          <ul className="navbar__links">{this.renderLabels()}</ul>
        </nav>
        <div className="events__filter-group">
          <DropdownRadioInput
            label={`${getMonthName(this.props.currentMonth.getMonth())} ${this.props.currentMonth.getFullYear()}`}
            counter={0}
            id="Septembre 2018"
            isShadow
            labelIcon="icon-calendar"
            options={this.getMonthsAsOptions(this.props.currentMonth)}
            inputChanged={this.props.handleMonthChanged}
            dropdownStyle={{ width: 220 }}
            title
            titleUpperCase
          />
        {activeLink === labels[0] && this.renderFilters()}
        {activeLink === labels[1] && this.renderFederalFilters()}
        </div>
      </Fragment>
    );
  };

  renderFiltersMobile = (classNamesFilters: string) => {
    return (
      <div className="filter-option">
        <button
          className={classNamesFilters}
          type="button"
          data-label="Fermer"
          onClick={this.handleToggleFilter}
        >
          Filtres
        </button>
        <div className="filter-option__list">
          {this.renderFilters()}
          <div className="filter-option__action">
            <ButtonIcon
              icon="icon-sync"
              noIcon
              isWhite
              url={null}
              label={null}
              onClick={this.props.handleResetFilters} />
            <ButtonIcon
              icon="icon-valid"
              noIcon
              isPrimary
              isInline
              label="Appliquer"
              url={null}
              onClick={this.handleToggleFilter}
            />
          </div>
        </div>
      </div>
    );
  }

  renderFederalFiltersMobile = (classNamesFilters: string) => {
    return (
      <div className="filter-option">
        <button
          className={classNamesFilters}
          type="button"
          data-label="Fermer"
          onClick={this.handleToggleFilter}
        >
          Filtre
        </button>
        <div className="filter-option__list">
          {this.renderFederalFilters()}
          <div className="filter-option__action">
            <ButtonIcon
              icon="icon-sync"
              noIcon
              isWhite
              url={null}
              label={null}
              onClick={this.props.handleResetFilters} />
            <ButtonIcon
              icon="icon-valid"
              noIcon
              isPrimary
              isInline
              label="Appliquer"
              url={null}
              onClick={this.handleToggleFilter}
            />
          </div>
        </div>
      </div>
    );
  }

  renderMobileNavbar = () => {
    const { activeLink } = this.state;
    const { labels } = this.props;
    const classNamesFilters = dynamicClassName('btn btn--white js-showFilter filter-option__cta ft-up');
    this.state.showFilters && classNamesFilters.add('is-active');

    return (
      <Fragment>
        <DropdownRadioInput
          label={this.state.activeLink}
          counter={0}
          id="labels_dropdown"
          isShadow
          options={labels.map(label => ({ value: label, label }))}
          inputChanged={this.handleLabelClicked}
          title
          titleUpperCase
        />

        <div className="events__filter-group">
          <DropdownRadioInput
            label={`${getMonthName(this.props.currentMonth.getMonth())} ${this.props.currentMonth.getFullYear()}`}
            counter={0}
            id="Septembre 2018"
            isShadow
            labelIcon="icon-calendar"
            options={this.getMonthsAsOptions(this.props.currentMonth)}
            inputChanged={this.props.handleMonthChanged}
            dropdownStyle={{ width: 220 }}
            title
            titleUpperCase
          />
          {activeLink === labels[0] && this.renderFiltersMobile(classNamesFilters.build())}
          {activeLink === labels[1] && this.renderFederalFiltersMobile(classNamesFilters.build())}
        </div>
      </Fragment>
    );
  };

  render() {
    return (
      <div className="events__filter events__filter--ffr">
        {this.state.menuType === TYPE_NORMAL && this.renderLargeNavbar()}
        {this.state.menuType === TYPE_MOBILE && this.renderMobileNavbar()}
      </div>
    );
  }
}

export default EventListNavbar;
